import { z } from 'zod'
import {
  TimeframeEnumValues,
  FdvAtTgePreferenceEnumValues,
  ExchangeGroupingFilterEnumValues,
  ExchangeTierGroupEnumValues,
  FloatBucketEnumValues,
  FloatBucketTimeframeEnumValues,
  ExchangePerformanceTimeframeEnumValues,
  ExchangeTypeEnumValues,
} from '@forgd/shared/enums'

// Shared query schema
export const PublicResearchTimeframeQuerySchema = z.object({
  timeframe: z.enum(TimeframeEnumValues),
})

export const PublicResearchDateRangeQuerySchema = z.object({
  startDate: z.string().date().optional(),
  endDate: z.string().date().optional(),
})

// TODO: for now it's here, but should be moved to shared/enums
export const CorrelationInterpretationEnumValues = [
  'Very Strong Positive',
  'Strong Positive',
  'Moderate Positive',
  'Weak Positive',
  'Very Weak',
  'Weak Negative',
  'Moderate Negative',
  'Strong Negative',
  'Very Strong Negative',
] as const

export const CorrelationInterpretationSchema = z.enum(CorrelationInterpretationEnumValues)

export const TokenMetaSchema = z.object({
  symbol: z.string(),
  name: z.string().optional(),
  logo: z.string().url().optional().nullable(),
})

// Price Change % | Initial Circ. Supply %
export const PriceChangeInitialCircSupplyTokenSchema = z.object({
  priceChangePercentage: z.number(),
  initialCircSupplyPercentage: z.number(),
})

export const PriceChangeInitialCircSupplyTimeframeDataSchema = z.object({
  tokens: z.array(PriceChangeInitialCircSupplyTokenSchema),
  correlation: z.number(),
  trend: z.array(PriceChangeInitialCircSupplyTokenSchema),
})

export const PriceChangeInitialCircSupplySummarySchema = z.object({
  timeframe: z.enum(TimeframeEnumValues),
  avgPriceChangePercentage: z.number().nullable(),
  correlation: z.number(),
  interpretation: CorrelationInterpretationSchema,
  description: z.string(),
})

export const PriceChangeInitialCircSupplyQuerySchema = PublicResearchDateRangeQuerySchema.extend({
  fdvBucket: z.enum(FdvAtTgePreferenceEnumValues).optional(),
})

export const PriceChangeInitialCircSupplyResponseSchema = z.object({
  timeframes: z.record(z.enum(TimeframeEnumValues), PriceChangeInitialCircSupplyTimeframeDataSchema),
  summary: z.array(PriceChangeInitialCircSupplySummarySchema),
  tokensQty: z.number(),
  tokenIndex: z.array(TokenMetaSchema),
})

// Price Change % | Annualised Inflation Rate %
export const PriceChangeInflationRateTokenSchema = z.object({
  symbol: z.string(),
  isCapped: z.boolean(),
  fdv: z.number(),
  circSupplyPct: z.number().nullable(),
  inflation24h: z.number().nullable(),
  inflation7d: z.number().nullable(),
  inflation30d: z.number().nullable(),
  inflation60d: z.number().nullable(),
  inflation90d: z.number().nullable(),
  inflationLatest: z.number().nullable(),
  inflationRate: z.number().nullable(),
  priceChange24h: z.number().nullable(),
  priceChange7d: z.number().nullable(),
  priceChange30d: z.number().nullable(),
  priceChange60d: z.number().nullable(),
  priceChange90d: z.number().nullable(),
  priceChangeLatest: z.number().nullable(),
})

export const PriceChangeInflationRateTimeframeDataSchema = z.object({
  avgPriceChange: z.number(),
  correlation: z.number(),
  interpretation: z.string(),
  description: z.string(),
  trendSlope: z.number(),
  trendIntercept: z.number(),
})

export const PriceChangeInflationRateSummarySchema = z.object({
  cappedCount: z.number(),
  uncappedCount: z.number(),
  avgInflationCapped: z.number(),
  avgInflationUncapped: z.number(),
})

export const PriceChangeInflationRateQuerySchema = PublicResearchDateRangeQuerySchema.extend({
  fdvBucket: z.enum(FdvAtTgePreferenceEnumValues).optional(),
})

export const PriceChangeInflationRateResponseSchema = z.object({
  summary: PriceChangeInflationRateSummarySchema,
  tokens: z.array(PriceChangeInflationRateTokenSchema),
  timeframes: z.record(z.enum(TimeframeEnumValues), PriceChangeInflationRateTimeframeDataSchema),
  tokensQty: z.number(),
  tokenIndex: z.array(TokenMetaSchema),
})

// Primary & Follow-On Listings, by Exchange
export const ListingsByExchangeQuerySchema = PublicResearchDateRangeQuerySchema.extend({
  exchangeGroup: z.enum(ExchangeTierGroupEnumValues).optional(),
})

export const ListingByExchangeSchema = z.object({
  exchangeName: z.string(),
  logo: z.string().nullable(),
  primaryListingsCount: z.number(),
  followOnListingsCount: z.number(),
})

export const ListingByMonthSchema = z.object({
  total: z.number(),
  month: z.string().nullable(),
  primaryCount: z.number(),
  followOnCount: z.number(),
})

// Allocation by Category
export const AllocationByCategoryQuerySchema = PublicResearchDateRangeQuerySchema.extend({
  fdvBucket: z.enum(FdvAtTgePreferenceEnumValues).optional(),
})

export const AllocationByCategorySchema = z.object({
  category: z.string(),
  avgAllocationPercentage: z.number(),
  avgAllocationUsd: z.number(),
})

export const AllocationByCategoryResponseSchema = z.object({
  categories: z.array(AllocationByCategorySchema),
  tokensQty: z.number(),
  tokenIndex: z.array(TokenMetaSchema),
})

export const DatedValuePointSchema = z.object({
  date: z.string().date(),
  value: z.number().nullable(),
})

// Average FDV by Initial Circulating Supply | Median FDV by Initial Circulating Supply
export const FdvByInitialCircSupplyGroupSchema = z.object({
  group: z.string(),
  averageFdv: z.number(),
  medianFdv: z.number(),
  // Weekly sparkline of average FDV at TGE. Populated for the "All" group only; sorted oldest→newest.
  averageFdvTimeSeries: z.array(DatedValuePointSchema).optional(),
})

export const FdvByInitialCircSupplyResponseSchema = z.object({
  groups: z.array(FdvByInitialCircSupplyGroupSchema),
  tokensQty: z.number(),
  tokenIndex: z.array(TokenMetaSchema),
})

export const PreExistingExchangePresenceResponseSchema = z.object({
  common: z.object({
    fdv: z.number(),
    medianFdv: z.number(),
    marketCap: z.number(),
    dailyVolume: z.number(),
    daysSinceTge: z.number(),
    priceChangeSinceTge: z.number(),
  }),
  perExchange: z.array(
    z.object({
      exchangeName: z.string(),
      logoUrl: z.string().nullish(),
      percentage: z.number(),
    }),
  ),
  fdvDistribution: z.array(
    z.object({
      label: z.string(),
      count: z.number(),
    }),
  ),
  adtvDistribution: z.array(
    z.object({
      label: z.string(),
      count: z.number(),
    }),
  ),
  tokensQty: z.number(),
  tokenIndex: z.array(TokenMetaSchema),
})

export const PreExistingExchangePresenceQuerySchema = PublicResearchDateRangeQuerySchema.extend({
  exchangeId: z.string(),
})

// Primary Listing Performance by Exchange (heatmaps)
export const ExchangePerformanceDataPointSchema = z.object({
  exchangeName: z.string(),
  timeframe: z.enum(ExchangePerformanceTimeframeEnumValues),
  value: z.number(),
})

// Per-exchange day-0 pop % and day-1 volume for primary listings.
// Present only in the per-exchange ("individual") response; values are null
// when an exchange has no qualifying tokens.
export const ExchangeStatsSchema = z.object({
  exchangeName: z.string(),
  avgInitialPopPercentage: z.number().nullable(),
  avgDay1VolumeUsd: z.number().nullable(),
})

// Price % Change of Primary Listings, by Exchange
export const PrimaryListingPerformanceDataPointSchema = z.object({
  daysPostTge: z.number(),
  priceChangePercentage: z.number(),
})

export const PrimaryListingPerformanceSeriesSchema = z.object({
  exchangeName: z.string(),
  data: z.array(PrimaryListingPerformanceDataPointSchema),
})

export const PrimaryListingPerformanceResponseSchema = z.object({
  series: z.array(PrimaryListingPerformanceSeriesSchema),
  avgPriceChangeByExchange: z.array(ExchangePerformanceDataPointSchema),
  avgFdvByExchange: z.array(ExchangePerformanceDataPointSchema),
  // Per-exchange pop/volume aggregates; present only for exchangeGroup=individual.
  exchangeStats: z.array(ExchangeStatsSchema).optional(),
  tokensQty: z.number(),
  tokenIndex: z.array(TokenMetaSchema),
})

export const PrimaryListingPerformanceQuerySchema = PublicResearchDateRangeQuerySchema.extend({
  exchangeGroup: z.enum(ExchangeGroupingFilterEnumValues),
})

// Median FDV by Initial Circulating Supply (%)
export const FdvVsInitialCircSupplyTokenSchema = z.object({
  initialCircSupplyPercentage: z.number(),
  fdv: z.number(),
})

export const FdvVsInitialCircSupplyDataSourceSchema = z.object({
  tokens: z.array(FdvVsInitialCircSupplyTokenSchema),
  correlation: z.number(),
  trend: z.array(FdvVsInitialCircSupplyTokenSchema),
})

export const FdvVsInitialCircSupplyLogScaleDataSourceSchema = z.object({
  tokens: z.array(FdvVsInitialCircSupplyTokenSchema),
  correlation: z.number(),
})

export const FdvVsInitialCircSupplyResponseSchema = z.object({
  linearScale: FdvVsInitialCircSupplyDataSourceSchema,
  logScale: FdvVsInitialCircSupplyLogScaleDataSourceSchema,
  tokensQty: z.number(),
  tokenIndex: z.array(TokenMetaSchema),
})

export const GetExchangesResponseSchema = z.array(
  z.object({
    id: z.string(),
    name: z.string(),
    logoUrl: z.string().nullable(),
    type: z.enum(ExchangeTypeEnumValues),
  }),
)
export type GetExchangesResponseType = z.infer<typeof GetExchangesResponseSchema>

export const GetExchangesQuerySchema = z.object({
  supported: z.string().optional(),
})
export type GetExchangesQueryType = z.infer<typeof GetExchangesQuerySchema>

// Price Performance by Float Bucket
export const AvgPricePerfByFloatBucketSchema = z.object({
  floatBucket: z.enum(FloatBucketEnumValues),
  timeframe: z.enum(FloatBucketTimeframeEnumValues),
  avgPriceChangePercentage: z.number().nullable(),
})

export const TokenPercentageByFloatBucketSchema = z.object({
  floatBucket: z.enum(FloatBucketEnumValues),
  timeframe: z.enum(FloatBucketTimeframeEnumValues),
  positiveReturnsPercentage: z.number().nullable(),
})

export const PriceChangeByFloatBucketDataPointSchema = z.object({
  daysPostTge: z.number(),
  avgPriceChangePercentage: z.number(),
})

export const PriceChangeByFloatBucketSeriesSchema = z.object({
  floatBucket: z.enum(FloatBucketEnumValues),
  data: z.array(PriceChangeByFloatBucketDataPointSchema),
})

export const PricePerformanceByFloatBucketQuerySchema = PublicResearchDateRangeQuerySchema.extend({
  fdvBucket: z.enum(FdvAtTgePreferenceEnumValues).optional(),
})

export const PricePerformanceByFloatBucketResponseSchema = z.object({
  avgPriceChangeByFloatBucket: z.array(AvgPricePerfByFloatBucketSchema),
  tokensWithPositiveReturnsByFloatBucket: z.array(TokenPercentageByFloatBucketSchema),
  priceChangeTimeSeries: z.array(PriceChangeByFloatBucketSeriesSchema),
  tokensQty: z.number(),
  tokenIndex: z.array(TokenMetaSchema),
})

// Follow-On Listing Impact
export const FollowOnListingImpactTimeframeDataSchema = z.object({
  listingBoostPct: z.number(),
  primaryChangePct: z.number(),
  secondaryChangePct: z.number(),
  volumeUsd: z.number(),
})

export const FollowOnListingImpactExchangeSchema = z.object({
  exchange: z.string(),
  logo: z.string().nullish(),
  tokenCount: z.number(),
  volumeAtListingUsd: z.number(),
  day0: FollowOnListingImpactTimeframeDataSchema,
  day1: FollowOnListingImpactTimeframeDataSchema,
  day7: FollowOnListingImpactTimeframeDataSchema,
  day30: FollowOnListingImpactTimeframeDataSchema,
})

export const FollowOnListingImpactResponseSchema = z.object({
  exchanges: z.array(FollowOnListingImpactExchangeSchema),
  tokensQty: z.number(),
  tokenIndex: z.array(TokenMetaSchema),
})

export const FollowOnListingImpactQuerySchema = PublicResearchDateRangeQuerySchema

// Top-3 Primary / Follow-On Listings tables (Exchange Intelligence overview).
export const TopListingsTokenRowSchema = z.object({
  rank: z.number(),
  symbol: z.string(),
  logoUrl: z.string().nullable(),
  listedOnExchange: z.string(),
  day1VolumeUsd: z.number().nullable(),
  priceChange7dPercentage: z.number().nullable(),
  priceChangeTimeSeries: z.array(DatedValuePointSchema),
})

export const TopListingsResponseSchema = z.object({
  tokens: z.array(TopListingsTokenRowSchema),
  tokensQty: z.number(),
  tokenIndex: z.array(TokenMetaSchema),
})

export const TopListingsQuerySchema = z.object({
  listingType: z.enum(['primary', 'follow-on']).optional(),
  timeframe: z.enum(TimeframeEnumValues).optional(),
  limit: z.number({ coerce: true }).int().positive().optional(),
})

export const PrEmissionScheduleAverageQuerySchema = z.object({
  nMonths: z.number({ coerce: true }).int().nonnegative(),
})

export const PrAggregateEmissionSchema = z.object({
  groupCategory: z.string(),
  monthIdx: z.number().int().nonnegative(),
  combinedCumulativePercentage: z.number(),
  combinedCumulativeSupply: z.number(),
  combinedCumulativeUsdValue: z.number(),
})

export const PrAggregateInflationSchema = z.object({
  monthIdx: z.number().int().nonnegative(),
  combinedInflationPercentage: z.number(),
})

export const PrAggregateInflationWithCategorySchema = PrAggregateInflationSchema.extend({
  category: z.string(),
})

const NullishNumberSchema = z
  .number()
  .nullish()
  .transform((val) => val ?? null)

export const PrEmissionStatsAverageSchema = z.object({
  avg1YearAnnualizedInflation: NullishNumberSchema,
  avg1YearCumulativeUnlock: NullishNumberSchema,
  avg1YearCumulativeUnlockUSD: NullishNumberSchema,
  avg2YearAnnualizedInflation: NullishNumberSchema,
  avg2YearCumulativeUnlock: NullishNumberSchema,
  avg2YearCumulativeUnlockUSD: NullishNumberSchema,
  avg3MonthAnnualizedInflation: NullishNumberSchema,
  avg3YearAnnualizedInflation: NullishNumberSchema,
  avg3YearCumulativeUnlock: NullishNumberSchema,
  avg3YearCumulativeUnlockUSD: NullishNumberSchema,
  avg6MonthAnnualizedInflation: NullishNumberSchema,
  avgAnnualizedInflationYears1To3: NullishNumberSchema,
  avgEmissionDuration: NullishNumberSchema,
  totalEmissionDuration: NullishNumberSchema,
  avgLockup: NullishNumberSchema,
  avgTgeUnlock: NullishNumberSchema,
  totalTgeUnlock: NullishNumberSchema,
  avgTgeUnlockUSD: NullishNumberSchema,
  totalTgeUnlockUSD: NullishNumberSchema,
  avgUnlock: NullishNumberSchema,
})

export const PrEmissionStatsWithCategorySchema = z.object({
  avg1YearAnnualizedInflation: NullishNumberSchema,
  avg1YearCumulativeUnlock: NullishNumberSchema,
  avg1YearCumulativeUnlockUSD: NullishNumberSchema,
  avg2YearAnnualizedInflation: NullishNumberSchema,
  avg2YearCumulativeUnlock: NullishNumberSchema,
  avg2YearCumulativeUnlockUSD: NullishNumberSchema,
  avg3MonthAnnualizedInflation: NullishNumberSchema,
  avg3YearAnnualizedInflation: NullishNumberSchema,
  avg3YearCumulativeUnlock: NullishNumberSchema,
  avg3YearCumulativeUnlockUSD: NullishNumberSchema,
  avg6MonthAnnualizedInflation: NullishNumberSchema,
  avgAnnualizedInflationYears1To3: NullishNumberSchema,
  avgEmissionDuration: NullishNumberSchema,
  avgLockup: NullishNumberSchema,
  avgTgeUnlock: NullishNumberSchema,
  avgTgeUnlockUSD: NullishNumberSchema,
  avgUnlock: NullishNumberSchema,
  category: z.string(),
})

export const PrEmissionDataPointSchema = z.object({
  monthIdx: z.number().int().nonnegative(),
  percent: z.array(z.number()),
  usd: z.array(z.number()),
  totalPercent: z.number(),
  totalUsd: z.number(),
})

export const PrEmissionScheduleAverageResponseSchema = z.object({
  emissions: z.array(PrAggregateEmissionSchema),
  groupedEmissions: z.array(PrEmissionDataPointSchema),
  categoryOrder: z.array(z.string()),
  inflation: z.object({
    avgAcrossCategories: z.array(PrAggregateInflationSchema),
    perCategory: z.array(PrAggregateInflationWithCategorySchema),
  }),
  emissionStats: z.object({
    avgAcrossCategories: PrEmissionStatsAverageSchema,
    perCategory: z.array(PrEmissionStatsWithCategorySchema),
  }),
})

// Initial Circulating Supply vs. Price Performance, Quartiles
export const InitialCircSupplyQuartilesDataPointSchema = z.object({
  q1: z.number(),
  q2: z.number(),
  q3: z.number(),
  q4: z.number(),
  daysPostTge: z.number(),
})

export const InitialCircSupplyQuartilesResponseSchema = z.object({
  greaterThan50: z.array(InitialCircSupplyQuartilesDataPointSchema),
  from25To50: z.array(InitialCircSupplyQuartilesDataPointSchema),
  from10To25: z.array(InitialCircSupplyQuartilesDataPointSchema),
  lessThan10: z.array(InitialCircSupplyQuartilesDataPointSchema),
  tokensQty: z.number(),
  tokenIndex: z.array(TokenMetaSchema),
})

export const InitialCircSupplyQuartilesQuerySchema = PublicResearchDateRangeQuerySchema.extend({
  fdvBucket: z.enum(FdvAtTgePreferenceEnumValues).optional(),
})

export const SankeyNodeSchema = z.object({
  id: z.string(),
  label: z.string(),
  stage: z.number(),
})

export const SankeyLinkSchema = z.object({
  source: z.number().int().nonnegative(),
  target: z.number().int().nonnegative(),
  value: z.number().int().nonnegative(),
})

export const SankeyRangeInfoSchema = z.object({
  low: z.string(),
  mid: z.string(),
  high: z.string(),
})

export const SankeyRangesSchema = z.object({
  fdv: SankeyRangeInfoSchema,
  float: SankeyRangeInfoSchema,
  performance: SankeyRangeInfoSchema,
})

export const SankeyConversionRatesSchema = z.object({
  lowFdvToStrongPop: z.number().nullish(),
  midFdvToStrongPop: z.number().nullish(),
  highFdvToStrongPop: z.number().nullish(),
  lowFloatToStrongPop: z.number().nullish(),
  midFloatToStrongPop: z.number().nullish(),
  highFloatToStrongPop: z.number().nullish(),
})

export const SankeyTopPathSchema = z.object({
  fdvCat: z.string(),
  floatCat: z.string(),
  perfCat: z.string(),
  count: z.number().int().nonnegative(),
  pct: z.number(),
})

export const LaunchSetupTokenJourneyResponseSchema = z.object({
  nodes: z.array(SankeyNodeSchema),
  links: z.array(SankeyLinkSchema),
  ranges: SankeyRangesSchema,
  conversionRates: SankeyConversionRatesSchema,
  topPaths: z.array(SankeyTopPathSchema),
  tokensQty: z.number(),
})

export const LaunchSetupTokenJourneyQuerySchema = PublicResearchDateRangeQuerySchema

// Airdrop Value vs Day-1 Price Performance
export const AirdropBubbleTokenSchema = z.object({
  symbol: z.string(),
  airdropUsd: z.number(),
  airdropUsdLog: z.number(),
  returnPct: z.number(),
  fdv: z.number(),
  airdropPct: z.number(),
})

export const AirdropScatterDataSchema = z.object({
  tokens: z.array(AirdropBubbleTokenSchema),
  correlation: z.number(),
  trendSlope: z.number(),
  trendIntercept: z.number(),
})

export const AirdropSummaryStatsSchema = z.object({
  meanAirdropUsd: z.number(),
  medianAirdropUsd: z.number(),
  meanAirdropPct: z.number(),
  maxAirdropUsd: z.number(),
})

export const AirdropValueVsPricePerformanceQuerySchema = PublicResearchDateRangeQuerySchema.extend({
  limit: z.number().optional(),
})

export const AirdropValueVsPricePerformanceResponseSchema = z.object({
  openBasis: AirdropScatterDataSchema,
  closeBasis: AirdropScatterDataSchema,
  summary: AirdropSummaryStatsSchema,
  tokensQty: z.number(),
  tokenIndex: z.array(TokenMetaSchema),
  // Weekly sparkline of the average airdrop value (USD, open basis) at TGE. Sorted oldest→newest.
  meanAirdropTimeSeries: z.array(DatedValuePointSchema).optional(),
})

export const MmMonitoringEngagementTypeSchema = z.enum(['Loan + Call Option', 'Retainer + Working Capital'])

export const MmTradeVolumeByEngagementTypeRowSchema = z.object({
  engagementType: MmMonitoringEngagementTypeSchema,
  avgMakerVolume: z.number(),
  avgTakerVolume: z.number(),
})
export const MmTradeVolumeByEngagementTypeResponseSchema = z.array(MmTradeVolumeByEngagementTypeRowSchema)

export const MmContributionsTimeseriesQuerySchema = z.object({
  metric: z.enum(['depth', 'volume']),
})
export const MmContributionsByTypeEntrySchema = z.object({
  engagementType: MmMonitoringEngagementTypeSchema,
  value: z.number(),
})
export const MmContributionsTimeseriesRowSchema = z.object({
  date: z.string(),
  byType: z.array(MmContributionsByTypeEntrySchema),
})
export const MmContributionsTimeseriesResponseSchema = z.array(MmContributionsTimeseriesRowSchema)

export const MmEngagementsSummaryEntrySchema = z.object({
  engagementType: MmMonitoringEngagementTypeSchema,
  count: z.number(),
})
export const MmEngagementsSummaryResponseSchema = z.object({
  total: z.number(),
  byType: z.array(MmEngagementsSummaryEntrySchema),
})

export const MmLoanUtilizationPointSchema = z.object({
  date: z.string(),
  utilizationPercent: z.number().nullable(),
})
export const MmLoanUtilizationResponseSchema = z.object({
  depth: z.array(MmLoanUtilizationPointSchema),
  volume: z.array(MmLoanUtilizationPointSchema),
})

export const MmSpreadsByFdvTierRowSchema = z.object({
  tier: z.enum(['High', 'Mid', 'Low']),
  avgSpreadPercent: z.number().nullable(),
})
export const MmSpreadsByFdvTierResponseSchema = z.array(MmSpreadsByFdvTierRowSchema)
